import { RecordingStatus, ShareType, useVideoRecordingStore, videoRecorder } from '@slot-nx/video-recorder'
import {
  ErrorType,
  FeatureToggleV2Type,
  Ga4EventType,
  ga4Info, preciseDivide,
  setErrorPopupData, useChipSetStore,
  useFeatureToggleV2Store,
} from '@slot-nx/core'
import { gameConfig } from '../../config'
import { useGameStore } from '../game/store/GameStore'
import { useGodziDropStore } from '../godzi/store/godziDropStore'
import { videoRecordService } from './index'

export class VideoRecordService {
  private isIOSMobile = /iPhone|iPad|iPod/i.test(navigator.userAgent)
  public get freeGameOdds(): number {
    return useGameStore.getState().gameResult.fgPayout > 0 ? preciseDivide(useGameStore.getState().gameResult.fgPayout, useChipSetStore.getState().computed.currentStake) : 0
  }

  public get godziOdds(): number {
    return useGodziDropStore.getState().payout > 0 ? preciseDivide(useGodziDropStore.getState().payout, useGodziDropStore.getState().effectiveStake) : 0
  }

  public get isEnableVideoRecording(): boolean {
    return useFeatureToggleV2Store.getState().toggleListV2.has(FeatureToggleV2Type.enableVideoRecord)
  }

  private startRecordTime: Date | null = null
  private interval: ReturnType<typeof setInterval> | null = null
  private limitRecordTime = 1000 * 60 * 1.5// 1.5 minutes
  public isForceCleanup = false

  public startRecording() {
    if (this.isIOSMobile) return
    const status = useVideoRecordingStore.getState().status
    const shouldRecording = this.freeGameOdds >= 50 || this.godziOdds >= 50
    if (this.isEnableVideoRecording && shouldRecording && status === RecordingStatus.Init) {
      this.isForceCleanup = false
      this.startRecordTime = new Date()
      videoRecorder.startRecording()
      videoRecordService.startWatchVideoRecord()
    }
  }

  public shareOrDownload() {
    if (this.isIOSMobile) return
    try {
      const status = useVideoRecordingStore.getState().status
      const isSharing = useVideoRecordingStore.getState().isSharing
      if (isSharing && status === RecordingStatus.RecordingDone) {
        const shareType = videoRecorder.getShareType()
        const errorHandler = (e: any) => {
          if (e instanceof Error) {
            if (e.name === 'AbortError') ga4Info.addEvent(Ga4EventType.record_video_abort)
            else {
              setErrorPopupData(ErrorType.SystemError)
              ga4Info.addEvent(Ga4EventType.record_video_error)
            }
          }
        }
        const finallyHandler = () => {
          useVideoRecordingStore.setState({
            isSharing: false,
            status: RecordingStatus.Init,
          })
        }

        if (shareType === ShareType.Share) {
          ga4Info.addEvent(Ga4EventType.record_video_share)
          videoRecorder.shareVideo(`${gameConfig.name}`).catch(errorHandler).finally(finallyHandler)
        }
        else if (shareType === ShareType.Download) {
          ga4Info.addEvent(Ga4EventType.record_video_download)
          videoRecorder.downloadVideo(gameConfig.name).catch(errorHandler).finally(finallyHandler)
        }

        videoRecorder.forceCleanup()
        if (this.interval) {
          clearInterval(this.interval)
          this.interval = null
        }
        this.startRecordTime = null
      }
    }
    catch (e) {
      console.error(e)
    }
  }

  public startWatchVideoRecord() {
    if (this.isIOSMobile) return
    const status = useVideoRecordingStore.getState().status
    if (status !== RecordingStatus.Recording) return
    this.interval = setInterval(() => {
      this.watchVideo()
    }, 1000)
  }

  private watchVideo() {
    if (this.isIOSMobile) return
    if (this.isForceCleanup || this.startRecordTime === null) return
    const currentTime = new Date()
    if (currentTime.getTime() - this.startRecordTime.getTime() >= this.limitRecordTime) {
      ga4Info.addEvent(Ga4EventType.record_video_over_time)
      videoRecorder.forceCleanup()
      this.isForceCleanup = true
      if (this.interval) {
        clearInterval(this.interval)
        this.interval = null
      }
      this.startRecordTime = null
    }
  }

  public stopRecordingInFreeGameTotalWin() {
    if (this.godziOdds >= 50) return
    this.stopRecording()
  }

  public stopRecordingInGodziTotalWin() {
    if (this.godziOdds < 50) return
    this.stopRecording()
  }

  private stopRecording() {
    if (this.isIOSMobile || this.startRecordTime === null || this.isForceCleanup) return
    const currentTime = new Date()
    if (currentTime.getTime() - this.startRecordTime.getTime() >= this.limitRecordTime) {
      ga4Info.addEvent(Ga4EventType.record_video_over_time)
      videoRecorder.forceCleanup()
      this.isForceCleanup = true
      if (this.interval) {
        clearInterval(this.interval)
        this.interval = null
      }
      this.startRecordTime = null
      return
    }
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
    }
    videoRecorder.stopRecording()
    useVideoRecordingStore.setState({
      isSharing: true,
    })
  }
}
